import { computed, Injectable, signal, inject } from '@angular/core';
import { IrisConfirmationService } from '@iris/common/services/confirmation.service';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { TranslateService } from '@ngx-translate/core';
import { IrisDmsModalsSandbox } from '../sandbox/dms-modals.sandbox';
import { IrisDmsFilesSandbox } from '../sandbox/dms-files.sandbox';
import { IrisDmsFoldersSandbox } from '../sandbox/dms-folders.sandbox';
import { forkJoin, from, lastValueFrom, Observable, of } from 'rxjs';
import { IrisDmsPermissionsSandbox } from '../sandbox/dms-permissions.sandbox';
import { IrisDmsFileUploadHelper } from './dms-file-upload.helper';
import { IrisDMSFolder } from '@iris/common/modules/dms/models/IrisDMSFolder';
import { Entry, FileEntry, WebkitFileI } from '@iris/common/models/FileSystem';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { IrisFilesService } from '@iris/common/modules/dms/services/files.service';
import { FileItem } from 'ng2-file-upload';
import { FileUploadState, IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { mapUploadingFilesIntoFolderTree, zipFolderTree$ } from '@iris/common/modules/dms/helpers/dropped-zipper.helper';
import { switchMap, tap, map as mapRxjs, takeUntil, take, finalize, filter, map, catchError } from 'rxjs/operators';
import { DirectoryEntry } from '@iris/common/models/FileSystem';
import { ImportingFolderTreeStage } from '@iris/common/modules/dms/models/ImportingFolderTree';
import {
  fileEntryGetFile,
  getDirectoriesEntries,
  getEntriesFromTransferItems,
  getFilesEntries,
  hasDirEntryChildDir,
  readEntriesAsync,
} from '@iris/common/utils/file-system-api';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { IrisTimePipe } from '@iris/common/modules/time';
import { IrisProjectsService } from '@iris/common/services/projects.service';
import { BulkFileUploader } from '../../file-uploader/bulk-file-uploader';
import { v4 as uuid } from 'uuid';
import { extendUrl } from '@iris/common/utils/url.utils';
import { DmsWebsocketService } from '@iris/common/services/websockets/dms-websocket.service';

export interface DmsDataTransferI {
  items?: DataTransferItem[];
  entries: Entry[];
  files?: File[];
  folderId: string;
  entitiesIds?: string[];
  directoryEntries: DirectoryEntry[];
  fileEntries: FileEntry[];
  dragId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class IrisDmsDataUploadHelper {
  private readonly dmsModalsSandbox = inject(IrisDmsModalsSandbox);
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly dmsPermissionsSandbox = inject(IrisDmsPermissionsSandbox);
  private readonly confirmationService = inject(IrisConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly dmsFileUploadHelper = inject(IrisDmsFileUploadHelper);
  private readonly filesService = inject(IrisFilesService);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly irisTimePipe = inject(IrisTimePipe);
  private readonly foldersService = inject(IrisFoldersService);
  private readonly projectsService = inject(IrisProjectsService);
  private readonly dmsWebsocketService = inject(DmsWebsocketService);

  readonly dragDataDropped = signal(false);
  readonly nameOfOverSizedFile = signal<string>('');

  readonly selectedFolderId = this.dmsFoldersSandbox.selectedFolderId;
  readonly selectedFolderName = computed(() => this.dmsFoldersSandbox.selectedFolder()?.name);

  private get fileUploader(): BulkFileUploader {
    return this.dmsFileUploadHelper.fileUploader;
  }

  private get maxFolderSizeInBytes(): number {
    return this.dmsFileUploadHelper.maxFileSizeInBytes();
  }

  public uploadFilesOrFolderWithFiles(
    dataTransfer: DmsDataTransferI,
    folderId = this.selectedFolderId(),
    folderName = this.selectedFolderName(),
  ): void {
    if (!dataTransfer.items.length) {
      return;
    }
    this.dragDataDropped.set(true);

    if (!dataTransfer.entries.length) {
      return;
    }

    const directoryEntries = dataTransfer.directoryEntries;
    const fileEntries = dataTransfer.fileEntries;

    if (fileEntries.length) {
      const dataTransferFiles: File[] = Array.from(dataTransfer.files).filter(file => {
        return fileEntries.some(fileEntry => fileEntry.name === file.name);
      });
      this.importFiles(dataTransferFiles, folderId);
    }

    if (!directoryEntries.length) {
      return;
    }

    if (directoryEntries.length > 1 || !!fileEntries.length) {
      return this.processMultipleDirectories(directoryEntries, folderId);
    }

    this.processUploadingDirectory(directoryEntries[0], folderId, folderName);
  }

  private processMultipleDirectories(directories: DirectoryEntry[], folderId: string): void {
    forkJoin(
      directories.map(directory => this.importFolderTree(directory, folderId)),
    ).pipe(
      take(1),
      tap(() => this.dmsFilesSandbox.reloadCurrentPage()),
    ).subscribe();
  }

  private processUploadingDirectory(directory: DirectoryEntry, folderId: string, folderName: string): void {
    hasDirEntryChildDir(directory).then(hasDirEntryChildDir => {
      if (hasDirEntryChildDir) {
        this.importFolderTree(directory, folderId).pipe(
          take(1),
          tap(() => this.dmsFilesSandbox.reloadCurrentPage()),
        ).subscribe();
        return;
      }
      this.importFolderWithFiles(directory, folderId, folderName);
    });
  }

  processSelectedFolder(files: WebkitFileI[]): void {
    const folderTree = mapUploadingFilesIntoFolderTree(files);
    const folderName = folderTree.name;
    const selectedFolderId = this.dmsFoldersSandbox.selectedFolderId();
    const rootFolderId  = this.dmsFoldersSandbox.currentProjectRootFolderId();
    const folderId = selectedFolderId || rootFolderId;
    if (folderId) {
      const importingFolderTrees = this.dmsFoldersSandbox.importingFolderTrees();
      if (importingFolderTrees.some(importingFolderTree => importingFolderTree.name === folderName)) {
        this.handleProcessSelectedFolderError(new Error('text.dms.YouAreAlreadyImportingAFolder'), folderName);
        return;
      }
      const folderThreeToImport = { name: folderName, stage: ImportingFolderTreeStage.Zipping, folderId };
      this.dmsFoldersSandbox.addImportingFolderTree(folderThreeToImport);
      this.dmsModalsSandbox.showUploadMinimizedModal(true);

      folderTree.generateZip$(this.maxFolderSizeInBytes).pipe(
        this.processZippedFolder(folderId, folderName),
        tap(() => {
          this.dmsFoldersSandbox.updateImportingFolderTree({
            name: folderName,
            stage: ImportingFolderTreeStage.Completed,
            folderId,
          });
          this.toastService.success(
            { message: this.translateService.instant('text.dms.FolderStructureWasImported', { name: folderName }) },
          );
        }),
        catchError((err) => {
          this.handleProcessSelectedFolderError(err, folderName);
          return of(null);
        }),
      ).subscribe();
    }
  }

  private handleProcessSelectedFolderError(err: Error, folderName: string): void {
    this.toastService.error({ message: this.translateService.instant(err.message) });
    this.dmsFoldersSandbox.removeImportingFolderTree(folderName);
  }

  public confirmCancelUploading(): Promise<boolean | string> {
    return this.confirmationService.show(this.translateService.instant('text.dms.DoYouWantToCancelUpload'), 'label.Yes', 'label.No');
  }

  private getMaxFolderSizeWarning(folderName: string): string {
    const notUploadedFolderMessage = this.translateService.instant('text.dms.FolderWasNotUploaded', { folderName });
    return this.dmsFileUploadHelper.getMaxSizeWarning(notUploadedFolderMessage, 'text.dms.MaxFolderSizeWarning');
  }

  private showMaxFolderSizeWarning(folderName: string): void {
    this.toastService.error({ message: this.getMaxFolderSizeWarning(folderName) });
  }

  private importFolderTree(item: DirectoryEntry, folderId: string): Observable<void> {
    let folderName = item.name;

    return from(this.getFolderNameAsync(folderId, folderName)).pipe(
      take(1),
      tap((_folderName) => {
        folderName = _folderName;
      }),
      filter(() => !!folderName),
      switchMap(() => {
        const importingFolderTrees = this.dmsFoldersSandbox.importingFolderTrees();
        const folderThreeToImport = { name: folderName, stage: ImportingFolderTreeStage.Zipping };
        if (importingFolderTrees.some(importingFolderTree => importingFolderTree.name === folderThreeToImport.name)) {
          throw new Error('text.dms.YouAreAlreadyImportingAFolder');
        }
        this.dmsFoldersSandbox.addImportingFolderTree(folderThreeToImport);
        this.dmsModalsSandbox.showUploadMinimizedModal(true);

        return zipFolderTree$(item, this.maxFolderSizeInBytes, folderName)
          .pipe(
            this.processZippedFolder(folderId, folderName),
          );
      }),
      tap(() => {
        this.dmsFoldersSandbox.updateImportingFolderTree({
          name: folderName,
          stage: ImportingFolderTreeStage.Completed,
          folderId,
        });
        this.toastService.success(
          { message: this.translateService.instant('text.dms.FolderStructureWasImported', { name: folderName }) },
        );
      }),
      catchError((err) => {
        this.toastService.error({ message: this.translateService.instant(err.message) });
        this.dmsFoldersSandbox.removeImportingFolderTree(folderName);
        return of(err);
      }),
    );
  }

  private processZippedFolder(folderId: string, folderName: string) {
    return (source: Observable<Blob>) => {
      return source.pipe(
        filter(zipBlob => {
          if (!zipBlob) {
            this.showMaxFolderSizeWarning(folderName);
            this.dmsFoldersSandbox.removeImportingFolderTree(folderName);
          }
          return !!zipBlob;
        }),
        mapRxjs((zipBlob) => new File([zipBlob], `${folderName}.zip`, { type: 'application/zip' })),
        tap(() => {
          this.dmsFoldersSandbox.updateImportingFolderTree({
            name: folderName,
            stage: ImportingFolderTreeStage.SendingToServer,
          });
        }),
        switchMap((zipFile) => this.filesService.importZipFile(folderId, zipFile)),
        takeUntil(
          this.dmsFoldersSandbox.folderTreeImportingCancellation$.pipe(
            take(1),
            tap(() => {
              this.dmsFoldersSandbox.removeImportingFolderTree(folderName);
              this.toastService.warning({ message: this.translateService.instant('text.dms.FolderUploadCanceled', { folderName }) });
            }),
          ),
        ),
        finalize(() => {
          this.dmsFoldersSandbox.expandFolder(folderId);
        }),
      );
    };
  }

  private confirmUploading(folderName: string, numberOfFiles: number, targetFolderName: string): Promise<boolean | string> {
    return this.confirmationService.show(this.translateService.instant(
      'text.dms.FolderWithFilesWillBeUploaded', { folderName, numberOfFiles, targetFolderName }),
    );
  }

  private confirmFolderRename(): Promise<boolean | string> {
    return this.confirmationService.show(
      this.translateService.instant('label.dms.FolderToUploadWithThisNameExists'),
      this.translateService.instant('label.dms.RenameFolder'),
      this.translateService.instant('label.CancelUpload'));
  }

  private checkIfFolderExists(folderId: string, name: string): Promise<boolean> {
    return lastValueFrom(this.foldersService.checkFolderExists(folderId, name));
  }

  private isFilesCanBeAdded(files: File[]): boolean {
    const nameOfOverSizedFile = this.dmsFileUploadHelper.checkNameOfOverSizedFile(files);
    this.nameOfOverSizedFile.set(nameOfOverSizedFile);
    return !nameOfOverSizedFile;
  }

  private importFiles(files: File[], folderId: string): void {
    const folder = this.dmsFoldersSandbox.getFolderById(folderId)();
    this.getProjectTimeZone(folder.projectId).pipe(take(1)).pipe(
      take(1),
      switchMap(timeZone => this.importFilesAsync(files, folderId, timeZone)),
    ).subscribe();
  }

  private importFilesAsync(files: File[], folderId: string, timeZone: string): Observable<boolean> {
    files = this.checkEmailFiles(files, timeZone);
    if (!this.isFilesCanBeAdded(files)) {
      return of(false);
    }
    this.dmsPermissionsSandbox.getUserAvailableActions(folderId);
    return this.hasDuplicateName$(files, folderId).pipe(
      tap((hasDuplicateName) => {
        const hasZipFile = this.hasZipFile(files);
        if (hasDuplicateName || hasZipFile) {
          this.dmsModalsSandbox.showUploadFilesModal(folderId);
        } else {
          this.uploadFilesAndShowMinimizedWindow(folderId, files);
        }
      }),
    );
  }

  private importFolderWithFiles(draggedFolder: DirectoryEntry, folderId: string, folderName: string): void {
    from(this.importFolderWithFilesAsync(draggedFolder, folderId, folderName)).pipe(
      take(1),
    ).subscribe();
  }

  private async importFolderWithFilesAsync(draggedFolder: DirectoryEntry, folderId: string, parentFolderName: string): Promise<void> {
    try {
      const entries = [...await readEntriesAsync(draggedFolder.createReader())];
      const filesEntries = getFilesEntries(entries);
      const filesPromises = filesEntries.map(entry => fileEntryGetFile(entry));
      const files = await Promise.all(filesPromises);
      const filesSize = files.reduce((sum, file) => sum + file.size, 0);

      const folderName = await this.getFolderNameAsync(folderId, draggedFolder.name);

      if (!folderName) {
        return;
      }

      if (filesSize > this.maxFolderSizeInBytes) {
        this.showMaxFolderSizeWarning(folderName);
        return;
      }

      await this.confirmUploading(folderName, files.length, parentFolderName);
      this.dmsPermissionsSandbox.getUserAvailableActions(folderId);

      return lastValueFrom(this.createFolderWithFiles(folderName, folderId, files));
    } catch (error) {
      return;
    }
  }

  private async getFolderNameAsync(folderId: string, folderName: string): Promise<string> {
    const folderExists = await this.checkIfFolderExists(folderId, folderName);

    if (folderExists) {
      await this.confirmFolderRename();

      const modal = this.dmsModalsSandbox.showFolderRenameModal(folderId, folderName);
      folderName = await lastValueFrom(modal.onSuccess$);
    }

    return Promise.resolve(folderName);
  }

  private createFolderWithFiles(folderName: string, folderId: string, files: File[]): Observable<void> {
    return this.foldersService.createFolder(new IrisDMSFolder({ name: folderName, parentId: folderId })).pipe(
      mapRxjs(({ id }) => {
        this.dmsFoldersSandbox.setSelectedFolder(id, false, true);

        const isFilesCanBeAdded = this.isFilesCanBeAdded(files);
        if (!isFilesCanBeAdded) {
          return;
        }

        this.uploadFilesAndShowMinimizedWindow(id, files);
      }),
    );
  }

  private uploadFilesAndShowMinimizedWindow(folderId: string, files: File[]): void {
    const uploadId = uuid();
    this.fileUploader.setOptions({
      url: extendUrl(this.filesService.getUploadUrl(folderId), `uploadId=${uploadId}`),
    });

    this.fileUploader
      .getNotUploadedItems()
      .forEach(file => file.uploadId = uploadId);

    let uploadedFiles = [];

    this.dmsFilesSandbox.addUploadingFiles(files);

    this.fileUploader.onSuccessItem = (file: FileItem, response: string): void => {
      const parsedResponse = JSON.parse(response);
      if (Array.isArray(parsedResponse)) {
        this.dmsFilesSandbox.updateUploadingFileState(file.file.name, FileUploadState.Completed);
        uploadedFiles = [...uploadedFiles, ...parsedResponse];
        this.addUploadedFiles(folderId, [...parsedResponse]);
        file.remove();
        this.dmsFilesSandbox.reloadCurrentPage();
      }
    };

    this.initUploaderErrorCallback();
    this.initUploaderCancelCallback();

    this.dmsModalsSandbox.showUploadMinimizedModal(true);
    this.fileUploader.uploadAllBulk();
    this.dmsWebsocketService.subscribeToUpload(uploadId);
  }

  private addUploadedFiles(folderId: string, uploadedFiles: IrisDMSFileI[]): void {
    if (folderId === this.selectedFolderId()) {
      this.dmsFilesSandbox.setUploadingFiles(uploadedFiles);
    } else {
      this.dmsFoldersSandbox.setSelectedFolder(folderId);
      this.dmsFoldersSandbox.expandFolder(folderId);
    }
  }

  private hasDuplicateName$(files: File[], folderId: string): Observable<boolean> {
    return this.dmsFilesSandbox.fileNamesExist(files.map(file => file.name), folderId).pipe(take(1));
  }

  private checkEmailFiles(files: File[], timeZone: string): File[] {
    return files.map(file => {
      if (this.filesService.isEmailFile(file.name)) {
        return new File(
          [file.slice(0, file.size, file.type)],
          `${this.irisTimePipe.transform(new Date(), 'YYYY-MM-DD_HHmmss', timeZone)}_${file.name}`,
          { type: file.type, lastModified: file.lastModified },
        );
      }
      return file;
    });
  }

  private hasZipFile(files: File[]): boolean {
    return files.some(file => this.filesService.isZipFile(file.name));
  }

  private initUploaderErrorCallback(): void {
    this.fileUploader.onErrorItem = (item: FileItem) => {
      this.dmsFilesSandbox.updateUploadingFileState(item.file.name, FileUploadState.Error);
    };
  }

  private initUploaderCancelCallback(): void {
    this.fileUploader.onCancelItem = (item: FileItem) => {
      this.cancelFileUploading(item);
    };
  }

  private cancelFileUploading(item: FileItem): void {
    const fileName = item.file.name;
    this.dmsFilesSandbox.updateUploadingFileState(fileName, FileUploadState.Canceled);
    this.toastService.warning({ message: this.translateService.instant('text.dms.FilesUploadCanceled', { fileName }) });
    this.dmsFilesSandbox.uploadingFiles$.pipe(
      filter(files => !files.length),
      take(1),
      tap(() => {
        this.dmsFilesSandbox.resetUploadingFiles();
        this.fileUploader.clearQueue();
      }),
    ).subscribe();
  }

  private getProjectTimeZone(projectId: number): Observable<string> {
    return this.globalSandbox.currentProject$.pipe(
      switchMap(currentProject => currentProject?.id === projectId ? of(currentProject) : this.projectsService.getProjectById(projectId).pipe(take(1))),
      map(project => project.timeZone),
    );
  }

  getDataTransferData(data: DataTransfer): DmsDataTransferI {
    const entries = getEntriesFromTransferItems(data.items);
    return {
      folderId: data.getData('folderId'),
      entitiesIds: data.getData('entitiesIds') ? data.getData('entitiesIds').split(',') : [],
      items: Array.from(data.items),
      entries,
      directoryEntries: getDirectoriesEntries(entries),
      fileEntries: getFilesEntries(entries),
      files: Array.from(data.files),
      dragId: data.getData('dragId'),
    };
  }
}
